import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { formatNumber } from '../admin/format';

export interface FunnelStage {
  key: string;
  label: ReactNode;
  value: number;
  hint?: ReactNode;
}

/**
 * How many made it from one step to the next — visitors, then people who
 * tried a product, then downloads, then leads. The share kept between two
 * steps is the number worth reading; the biggest drop is marked.
 */
export function Funnel({ stages, className, testId }: { stages: FunnelStage[]; className?: string; testId?: string }) {
  const { t } = useTranslation();
  const first = stages[0]?.value ?? 0;
  const kept = stages.map((s, i) => (i === 0 || !stages[i - 1].value ? null : s.value / stages[i - 1].value));
  // The step that loses the most people, if any step loses them.
  let worst = -1;
  kept.forEach((k, i) => {
    if (k != null && k < 1 && (worst < 0 || k < (kept[worst] ?? 1))) worst = i;
  });

  return (
    <ol className={cn('flex flex-col', className)} data-testid={testId}>
      {stages.map((s, i) => {
        const width = first > 0 ? Math.max(1.5, (s.value / first) * 100) : 0;
        return (
          <li key={s.key}>
            {i > 0 && (
              <div className="flex items-center gap-2 py-1.5 ps-3 text-xs text-muted-foreground">
                <span className="h-4 border-s border-dashed" aria-hidden />
                {kept[i] == null ? (
                  '—'
                ) : (
                  <span className={cn('tabular-nums', i === worst && 'font-medium text-destructive')}>
                    {t('ANALYTICS_KIT.FUNNEL_KEPT', { percent: Math.round((kept[i] as number) * 100) })}
                    {i === worst && ` · ${t('ANALYTICS_KIT.FUNNEL_BIGGEST_DROP')}`}
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center gap-4">
              <div className="w-36 shrink-0 sm:w-44">
                <p className="truncate text-sm">{s.label}</p>
                {s.hint && <p className="truncate text-xs text-muted-foreground">{s.hint}</p>}
              </div>
              <div className="relative h-8 flex-1 overflow-hidden rounded-md bg-muted/60">
                <div className="h-full rounded-md bg-foreground/80 transition-[width] duration-500" style={{ width: `${width}%`, opacity: 1 - i * 0.14 }} />
              </div>
              <span className="w-16 shrink-0 text-end text-sm font-medium tabular-nums">{formatNumber(s.value)}</span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
